import { ArrayToObject } from 'helpers/arrays';
import { isCarrierDisabledByAgencyPreferences } from 'helpers/carriers/carriers';
import { getCompany } from 'helpers/getCompanyVariables';
import {
  getStateCodeFromStateName,
  getStateNameFromStateCode
} from 'helpers/states';

const OEP_UPDATE_TYPES = new Set(['Expansion', 'Exit']);
// `New State` is expansion-only: the carrier is entering a state it doesn't
// currently offer. it may still carry counties when the carrier only enters
// part of the state. `Whole State` stays available for both types (an exit
// from an entire state, or a statewide expansion of an already-offered state).
const OEP_SCOPE_VALUES = new Set(['New State', 'Whole State', 'Counties Only']);

const company = getCompany();

export function carrierFactory(carrier, user) {
  const availableResidentStates = new Set(
    Array.isArray(carrier.Available_Resident_States)
      ? carrier.Available_Resident_States
      : []
  );

  const availableAgentRanks = new Set(
    Array.isArray(carrier.Available_Agent_Ranks)
      ? carrier.Available_Agent_Ranks
      : []
  );

  // the inverse of Available_Agent_Ranks: these ranks don't get the carrier at all
  const unavailableAgentRanks = new Set(
    Array.isArray(carrier.Unavailable_Agent_Ranks)
      ? carrier.Unavailable_Agent_Ranks
      : []
  );

  const userResidentState = user.Resident_Insurance_License_State || '';

  const isResidentStateInvalid =
    availableResidentStates.size > 0 &&
    !availableResidentStates.has(userResidentState);

  const isRankUnavailable = unavailableAgentRanks.has(user.Rank);

  const exclude =
    isResidentStateInvalid ||
    isRankUnavailable ||
    (company === 'sunshine' &&
      ((carrier.Appointment_Type === 'Marketplace' && // additional check for marketplace carriers to avoid other types of carriers to be excluded
        ((user.agencyData.Hide_ACA && carrier.PrimaryCarrier === true) ||
          (user.agencyData.Hide_ACA_Other &&
            carrier.PrimaryCarrier === false))) ||
        (carrier.Appointment_Type === 'Life' &&
          user.agencyData.Hide_Long_term_Health &&
          carrier.Name.includes('Long')) ||
        (carrier.Appointment_Type === 'Life' &&
          user.agencyData.Hide_Disability &&
          carrier.Name.includes('Disability')) ||
        (carrier.Appointment_Type === 'Life' &&
          user.agencyData.Hide_Annuity &&
          carrier.Name.includes('Annuity'))));

  const agencyDisabled = isCarrierDisabledByAgencyPreferences(carrier, user);

  return {
    id: carrier.id,
    exclude,
    agencyDisabled,
    disabledByUpline: !!carrier.Disabled_By_Upline,
    name: carrier.Name,
    type: carrier.Appointment_Type,
    description: carrier.Description,
    Callidus_Carrier_Names: carrier?.Callidus_Carrier_Names?.split(',')?.map(
      c => c.trim()
    ),
    carrierBrokerPortal: carrier.Carrier_Broker_Portal,
    carrierFAQ: carrier.Carrier_FAQ,
    enrollmentPortal: carrier.Enrollment_Portal,
    brokerSupportNumber: carrier.Broker_Support_Number,
    Bob_Agent_Support_Email: carrier.BOB_Agent_Support_Email,
    memberSupportNumber: carrier.Member_Support_Number,
    Broker_Support_Email: carrier.Broker_Support_Email,
    Broker_Commission_Email: carrier.Broker_Commission_Email,
    omWebsiteLink: carrier.OM_Website_Link,
    website: carrier.Website,
    AgencyBloc_CarrierID: carrier.AgencyBloc_CarrierID,
    statesOffered: carrier.States_Offered,
    primaryCarrier: carrier.PrimaryCarrier,
    customNoteNewAppointment: carrier.Custom_Note_New_Appointment,
    customNoteTransferDirectwithCarrier:
      carrier.Custom_Note_Transfer_Direct_with_Carrier,
    customNoteTransferThroughAnotherAgency:
      carrier.Custom_Note_Transfer_Through_Another_Agency,
    carrierFeatureDescriptionNotes: carrier.Carrier_Feature_Description_Notes,
    appointmentsDisabled: !!carrier.Appointments_Unavailable,
    // Required Actions Info for Sunshine
    customFileTitle: carrier.Custom_File_Title,
    ffmIdFile: carrier.FFM_ID_File,
    driversLicenseFile: carrier.Driver_s_License_File,
    stateBasedExchangeCertificateFile:
      carrier.State_Based_Exchange_Certificate_File,
    stateInsuranceLicenseFile: carrier.State_Insurance_License_File,
    customFileStates: carrier.Custom_File_State_Based,
    // New Appointment Required Actions Info for O'Neill
    LOAFileRequirement: carrier.LOA_FIle_Requirement,
    LOAStates: carrier.LOA_Required_States,
    SBEFileRequirement: carrier.SBE_File_Requirement,
    SBEStates: carrier.SBE_Required_States,
    PaperContractRequirement: carrier.Paper_Contract_File_Requirement,
    PaperContractURL: carrier.Paper_Contract_URL,
    floridaStateLicense: carrier.Florida_State_License,
    acknowledgementNote: carrier.Acknowledgement_Note,
    BCBSNCPaperContractURL: carrier.BCBS_NC_Paper_Contract_URL,
    BCBSSCPaperContractURL: carrier.BCBS_SC_Paper_Contract_URL,
    ffmCertificate: carrier.FFM_Certificate,
    eoPolicy: carrier.E_O_Policy,
    // Transfer Forms Required Actions Info for O'Neill
    agencyFormURL: carrier.Agency_Transfer_Form_URL,
    directFormURL: carrier.Direct_Transfer_Form_URL,
    BCBSTNFormURL: carrier.BCBS_TN_Transfer_Form_URL,
    BCBSTXFormURL: carrier.BCBS_TX_Transfer_Form_URL,
    BCBSTNTransferNote: carrier.BCBS_TN_Transfer_Note,
    BCBSTXTransferNote: carrier.BCBS_TX_Transfer_Note,
    transferNote: carrier.Transfer_Note,
    transferBlackout: carrier.Blackout_for_transfers,
    contractingBlackout: carrier.Blackout_for_contracting,
    customNoteAddStates: carrier.Custom_Note_Adding_States,
    considerStates: carrier.Carrier_Audit_Consider_States,
    username: carrier.Username,
    password: carrier.Password,
    enableStateRequestsIfAppointed:
      !!carrier.Enable_State_Requests_After_Agent_Is_Appointed,
    image_url: carrier.Image_URL,
    appointmentGroup: carrier.Carrier_Appointments_Group,
    appointmentParentGroup: carrier.Carrier_Appointments_Parent_Group,
    newAppointmentsVideoURL: carrier.New_Appointments_Video_URL,
    carrierIcon: carrier.Carrier_Icon,
    carrierIconColor: carrier.Carrier_Icon_Color,
    carrierIconDescription: carrier.Carrier_Icon_Description,
    workDriveFolderURL: carrier.WorkDrive_Folder,
    commissionsColor: carrier.Carrier_Commissions_Color,
    highlightCarrier: !!carrier.Highlight_Carrier,
    highlightMessage: carrier.Highlight_Message ?? '',
    emptyHighlightMessage: carrier.Empty_Highlight_Message ?? '',
    // disclaimer
    disclaimer: normalizeDisclaimer(carrier),
    ignoreSbeAndFfmLabels: carrier.Ignore_SBE_and_FFM_State_Labels,
    allowTransferTypeSelection:
      !!carrier.Allow_RA_Transfer_Type_Selection_When_Appointed,
    availableResidentStates,
    availableAgentRanks,
    // oep updates
    oepHidden: !!carrier.Hide_from_OEP_Updates,
    oepResourcesWorkDriveURL: carrier.OEP_Resources_WorkDrive_URL,
    // free-form note the carrier wants to highlight this OEP. zoho hands it back
    // as html (links, <br>, entities), so it's kept raw for RichTextContent
    oepNotes: carrier.OEP_Carrier_Notes || '',
    oepStateUpdates: normalizeOepStateUpdates(carrier.OEP_Expansions_Exit)
  };
}

// normalizes the OEP_Expansions_Exit subform rows so the view can render them
// without re-parsing per component. drops rows missing required fields, and
// derives a compact `status` label from the (type, scope) tuple so the UI
// doesn't need to duplicate the mapping.
function normalizeOepStateUpdates(rows) {
  if (!Array.isArray(rows)) return [];
  const normalized = [];
  for (const row of rows) {
    if (!row) continue;
    const type = row.Type;
    if (!OEP_UPDATE_TYPES.has(type)) continue;
    const rawState = row.State;
    if (!rawState) continue;
    // zoho state picklists may store the code, the full name, or the label; try
    // both directions and keep whichever resolves to a real state
    const uppercaseRaw = String(rawState).toUpperCase();
    let stateCode = getStateNameFromStateCode(uppercaseRaw)
      ? uppercaseRaw
      : null;
    let stateName = stateCode ? getStateNameFromStateCode(stateCode) : null;
    if (!stateCode) {
      const codeFromName = getStateCodeFromStateName(rawState);
      if (codeFromName) {
        stateCode = codeFromName;
        stateName = getStateNameFromStateCode(codeFromName);
      }
    }
    if (!stateCode) continue;
    const scope = OEP_SCOPE_VALUES.has(row.Scope) ? row.Scope : null;
    // whole-state rows never carry meaningful counties (the whole state is
    // affected). force to '' so the ui never leaks stale text left over from
    // a scope change before the zoho guard fires. `New State` is exempt: a
    // carrier can enter a brand-new state in only a handful of counties.
    const rawCounties = scope === 'Whole State' ? '' : row.Counties || '';
    normalized.push({
      id: `${type}:${stateCode}:${normalized.length}`,
      type,
      scope,
      status: deriveOepStatus(type, scope),
      stateCode,
      stateName: stateName || stateCode,
      counties: rawCounties
    });
  }
  return normalized;
}

// maps (type, scope) to a compact status label. missing/unknown scope falls
// back to 'Updated' so partially-configured rows still render sensibly during
// the rollout of the Scope column. `New State` paired with an Exit is an
// invalid combination zoho should block, so it degrades to 'Updated' rather
// than claiming the carrier both entered and left the state.
function deriveOepStatus(type, scope) {
  if (type === 'Expansion') {
    if (scope === 'New State') return 'Added';
    if (scope === 'Whole State') return 'Added';
    return 'Updated';
  }
  if (type === 'Exit') {
    if (scope === 'Whole State') return 'Removed';
    return 'Updated';
  }
  return 'Updated';
}

export const DISCLAIMER_PLACEMENTS = {
  APPOINTED_SIDEBAR_CONFIRMED: 'Appointed Sidebar - Confirmed Appointments',
  APPOINTED_SIDEBAR_OPEN: 'Appointed Sidebar - Open Requests',
  NOT_APPOINTED_SIDEBAR: 'Not Appointed Sidebar',
  AVAILABLE_STATES_MAP: 'Available States Map',
  // these strings must match the Disclaimer_Placements picklist values in zoho
  // character for character: normalizeDisclaimer whitelists against them, so a
  // mismatch silently drops the disclaimer instead of raising anything
  OEP_UPDATES_TAB: 'OEP Updates Tab'
};

export const DISCLAIMER_ICONS = {
  INFO: 'Info',
  WARNING: 'Warning'
};

export const DISCLAIMER_COLORS = {
  BLUE: 'Blue',
  RED: 'Red',
  GREEN: 'Green',
  YELLOW: 'Yellow',
  GRAY: 'Gray'
};

const ALLOWED_DISCLAIMER_PLACEMENTS = new Set(
  Object.values(DISCLAIMER_PLACEMENTS)
);

const ALLOWED_DISCLAIMER_ICONS = new Set(Object.values(DISCLAIMER_ICONS));

const ALLOWED_DISCLAIMER_COLORS = new Set(Object.values(DISCLAIMER_COLORS));

function normalizeDisclaimer(carrier) {
  const text = carrier.Disclaimer_Text;
  if (!text) return null;

  const placements = new Set();

  if (
    carrier.Disclaimer_Placements &&
    Array.isArray(carrier.Disclaimer_Placements)
  ) {
    carrier.Disclaimer_Placements.forEach(placement => {
      if (ALLOWED_DISCLAIMER_PLACEMENTS.has(placement))
        placements.add(placement);
    });
  }

  if (placements.size === 0) return null;

  const icon = ALLOWED_DISCLAIMER_ICONS.has(carrier.Disclaimer_Icon)
    ? carrier.Disclaimer_Icon
    : 'Info';
  const color = ALLOWED_DISCLAIMER_COLORS.has(carrier.Disclaimer_Color)
    ? carrier.Disclaimer_Color
    : 'Blue';

  return {
    text,
    color,
    placements,
    icon
  };
}

export function carriersObjectFactory(carriers) {
  // filters carriers by type
  const Marketplace = carriers.filter(
    carrier => carrier.type === 'Marketplace'
  );

  const OffMarketHealth = carriers.filter(
    carrier => carrier.type === 'Off-Market Health'
  );

  const Life = carriers.filter(carrier => carrier.type === 'Life');
  const Medicare = carriers.filter(carrier => carrier.type === 'Medicare');
  const Group = carriers.filter(carrier => carrier.type === 'Group');

  // build the carriers object for redux state
  return {
    Marketplace: ArrayToObject(Marketplace, 'name'),
    'Off-Market Health': ArrayToObject(OffMarketHealth, 'name'),
    Life: ArrayToObject(Life, 'name'),
    Medicare: ArrayToObject(Medicare, 'name'),
    Group: ArrayToObject(Group, 'name')
  };
}
